import "./styles/scss/style.scss";
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Home from "./layouts/Home";
import Login from "./components/auth/Login";
import Dashboard from "./components/dashboard/Dashboard";
import { RouterPathEnum } from "./constants/router";
import ProtectedRoute, { ProtectedRouteProps } from "./ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import AppToast from "./components/common/ui/AppToast";
import { useDispatch, useSelector } from "react-redux";
import { hideSnackbar } from "./store/actions/snackbarActions";
import InterventionLabelContainer from "./components/admin/intervention-labels/InterventionLabelContainer";
import AcronymContainer from "./components/admin/acronyms/AcronymContainer";
import MeetingsContainer from "./components/admin/MeetingsContainer";
import UserManagementContainer from "./components/admin/UserManagementContainer";
import MasterLayout from "./layouts/MasterLayout";
import UnauthorizedUser from "./components/auth/UnauthorizedUser";
import AccessDenied from "./components/auth/AccessDenied";
import PageNotFound from "./components/auth/PageNotFound";
import UserRequestForm from "./components/auth/UserRequestForm";
import UserInactive from "./components/auth/UserInactive";
import AxiosInterceptorSetup from "./components/AxiosInterceptorSetup";

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { accessToken } = useAuth();

  const snackbar = useSelector((state: any) => state.snackbar);

  // close toast message on close icon click
  const handleCloseToast = () => {
    dispatch(hideSnackbar());
  };

  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    isAuthenticated: accessToken ? true : false,
    authenticationPath: `/${RouterPathEnum.LOGIN}`,
  };

  return (
    <>
      <AxiosInterceptorSetup />
      <Routes>
        {/* If user is logged in and hits '/', send to dashboard */}
        <Route
          path="/"
          element={
            <Navigate
              to={
                accessToken
                  ? `/${RouterPathEnum.DGO}/${RouterPathEnum.DASHBOARD}`
                  : `/${RouterPathEnum.LOGIN}`
              }
              replace
            />
          }
        />

        <Route path={RouterPathEnum.LOGIN} element={<Login />} />

        <Route path={RouterPathEnum.DGO} element={<MasterLayout />}>
          <Route
            path={RouterPathEnum.DASHBOARD}
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={<Dashboard />}
              />
            }
          />
          <Route
            path={`${RouterPathEnum.HOME}/:id/:meetingId`}
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={<Home />}
              />
            }
          />

          <Route path={RouterPathEnum.ADMIN}>
            <Route
              path={RouterPathEnum.MEETINGS}
              element={
                <ProtectedRoute
                  {...defaultProtectedRouteProps}
                  outlet={<MeetingsContainer />}
                />
              }
            />
            <Route
              path={RouterPathEnum.INTERVENTION_LABEL}
              element={
                <ProtectedRoute
                  {...defaultProtectedRouteProps}
                  outlet={<InterventionLabelContainer />}
                />
              }
            />
            <Route
              path={RouterPathEnum.ACRONYMS}
              element={
                <ProtectedRoute
                  {...defaultProtectedRouteProps}
                  outlet={<AcronymContainer />}
                />
              }
            />
            <Route
              path={RouterPathEnum.USER_MANAGEMENT}
              element={
                <ProtectedRoute
                  {...defaultProtectedRouteProps}
                  outlet={<UserManagementContainer />}
                />
              }
            />
          </Route>
        </Route>

        <Route
          path={RouterPathEnum.UNAUTHORIZED}
          element={<UnauthorizedUser />}
        />
        <Route
          path={RouterPathEnum.ACCESS_DENIED}
          element={<AccessDenied />}
        />
        <Route
          path={RouterPathEnum.PAGE_NOT_FOUND}
          element={<PageNotFound />}
        />
        <Route
          path={RouterPathEnum.USER_REQUEST}
          element={<UserRequestForm />}
        />
        <Route
          path={RouterPathEnum.USER_INACTIVE}
          element={<UserInactive />}
        />

        {/* Catch-all route for undefined paths - must be last */}
        <Route path="*" element={<Navigate to={`/${RouterPathEnum.PAGE_NOT_FOUND}`} replace />} />
      </Routes>
      <AppToast
        open={snackbar.open}
        onClose={handleCloseToast}
        message={String(snackbar.message)
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/\. /g, ".\n")}
        severity={snackbar.severity}
        autoHideDuration={5000}
      />
    </>
  );
};

export default App;
